import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { apiErrorMessage } from '../lib/errors';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoMark } from '../components/icons';
import { GoogleErrorNotice, GoogleSignInButton } from '../components/auth/GoogleSignInButton';

interface LocationState {
  from?: string;
}

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as LocationState | null)?.from ?? '/dashboard';

  // A failed Google redirect comes back as ?error=<reason>. It is read from the URL
  // rather than from state because the callback is a full-page navigation.
  const googleError = new URLSearchParams(location.search).get('error');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError(t('login.errorRequired'));
      return;
    }

    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (caught) {
      setError(apiErrorMessage(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16">
      <div className="mb-6 flex flex-col items-center text-center">
        <LogoMark className="h-12 w-12" />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-fg">{t('login.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('login.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('login.cardTitle')}</CardTitle>
          <CardDescription>{t('login.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {googleError && <GoogleErrorNotice reason={googleError} />}

            {/* Google first: it is the primary way in, and the password form below
                is the fallback that keeps staff and pre-existing accounts working. */}
            <GoogleSignInButton />

            <div className="flex items-center gap-3" role="separator" aria-label={t('login.or')}>
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-wide text-muted">{t('login.or')}</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              {error && <Alert tone="error">{error}</Alert>}

              <p className="text-xs text-muted">{t('login.staffNote')}</p>

              <div className="space-y-1.5">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  aria-invalid={error ? true : undefined}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-invalid={error ? true : undefined}
                  required
                />
              </div>

              <Button type="submit" size="lg" fullWidth loading={submitting}>
                {t('login.submit')}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      <p className="mt-6 text-center text-sm text-muted">
        {t('login.noAccount')}{' '}
        <Link to="/register" className="font-medium text-brand hover:underline">
          {t('login.createOne')}
        </Link>
      </p>
    </div>
  );
}
